/**
 * Production: making a batch of prep, and putting it on a shelf at what it cost.
 *
 * Three things a screen built on this has to get right, because the API is
 * careful about them and a UI can undo that carelessly.
 *
 * **A run has two endings and they are opposites.** `cancel` drops a plan
 * nobody got to and costs nothing. `abandon` gives up on a run already under
 * way, and the ingredients are gone — they went in the pot when it started and
 * there is nothing to put back. They look alike on a screen and must not read
 * alike; the API refuses the wrong one with a message saying which to press
 * instead, and that message is worth showing verbatim.
 *
 * **`actual_yield` has no default, on purpose.** A pre-filled figure a chef
 * confirms without measuring turns the variance report into a report on how
 * often people press the obvious button. Leave the field empty.
 *
 * **`unit_cost` is `string | null`, and null means the run yielded nothing.**
 * Not free. A pot went on the floor, the whole input cost stayed spent, and
 * showing "£0.00 per litre" says the opposite of what happened.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export type RunState = 'planned' | 'in_progress' | 'completed' | 'abandoned' | 'cancelled';

/**
 * Five states, and the labels say what happened rather than naming the state.
 *
 * "Abandoned" and "Cancelled" are deliberately different words for the two
 * endings — see the note at the top. A kitchen reading a list needs to tell at
 * a glance which runs cost money.
 */
export const RUN_STATE_LABELS: Record<RunState, string> = {
  planned: 'Planned',
  in_progress: 'Cooking',
  completed: 'Made',
  abandoned: 'Abandoned',
  cancelled: 'Cancelled',
};

export interface ProductionInput {
  stock_item_id: string;
  name: string;
  unit: string;
  quantity: string;
  /** What the ledger charged. Null on a planned run — nothing has moved yet. */
  cost: string | null;
}

export interface ProductionRun {
  id: string;
  recipe_id: string;
  recipe_name: string;
  produces_item_id: string;
  location_id: string;
  storage_id: string;
  state: RunState;
  batches: string;
  expected_yield: string;
  /** What was measured. Null until the run finishes. */
  actual_yield: string | null;
  yield_unit: string;
  /**
   * What `actual_yield` was measured in, when it is not `yield_unit`: only a
   * stranded run finished in the shelf's own unit. Such a run has no yield
   * variance — kilos out of a recipe written in litres compare with nothing.
   */
  actual_unit: string | null;
  /** The unit the item this run makes is held in. */
  stock_unit: string;
  /**
   * Under way, and cannot be booked in in its recipe's unit — the item it makes
   * cannot take that unit, and the ingredients are already off the shelf. The
   * way out is finishing it in `stock_unit` (`inStockUnit`), not abandoning it:
   * the food exists.
   */
  stranded: boolean;
  /** What left the store, in money. Null until the run starts. */
  input_cost: string | null;
  /** Null on a run that yielded nothing. Never zero — see the note at the top. */
  unit_cost: string | null;
  /** Negative is short, positive is over. Both are variance. */
  yield_variance_percent: string | null;
  /**
   * Value the shortfall pushed onto the units that did come out.
   *
   * **Not a loss, and it must not be labelled as one.** Every penny is still
   * in the stockroom; what changed is that fewer units carry it, so each is
   * worth more and every dish made from them costs more. That consequence is
   * invisible on a stock report, which is the whole reason to show it.
   */
  absorbed_cost: string;
  started_at: string | null;
  finished_at: string | null;
  note: string | null;
  abandon_reason: string | null;
  inputs: ProductionInput[];
}

export interface PlanRunInput {
  recipeId: string;
  locationId: string;
  storageId: string;
  batches?: string;
  note?: string;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['production'] });
  // Every run moves stock, so the levels a screen is showing are now stale.
  void queryClient.invalidateQueries({ queryKey: ['inventory'] });
}

/**
 * Runs at a location, newest first.
 *
 * Filter by `in_progress` for what is on the go and `planned` for this
 * morning's prep list. Ordered by when each run was planned rather than when
 * it started, so tomorrow's list does not sort below work already done.
 */
export function useProductionRuns(locationId: string | undefined, state?: RunState) {
  const api = useApi();
  return useQuery({
    queryKey: ['production', 'runs', locationId ?? null, state ?? null],
    queryFn: ({ signal }) =>
      api.get<ProductionRun[]>(
        `/v1/production/runs?location_id=${locationId}${state ? `&state=${state}` : ''}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 15_000,
  });
}

export function useProductionRun(runId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['production', 'run', runId ?? null],
    queryFn: ({ signal }) => api.get<ProductionRun>(`/v1/production/runs/${runId}`, signal),
    enabled: Boolean(runId),
    staleTime: 15_000,
  });
}

/**
 * Write a run down before doing it. Nothing moves.
 *
 * The ingredient list is worked out and frozen here, so the run draws what it
 * said it would even if somebody edits the recipe overnight — which means the
 * returned `inputs` are a prep sheet that can be printed and relied on.
 */
export function usePlanRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ProductionRun, ApiError, PlanRunInput>({
    mutationFn: (input) =>
      api.post<ProductionRun>('/v1/production/runs', {
        recipe_id: input.recipeId,
        location_id: input.locationId,
        storage_id: input.storageId,
        batches: input.batches ?? '1',
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Draw the ingredients and get cooking. Stock leaves the shelf now.
 *
 * Now rather than at the end, because now is when it physically leaves — a
 * stock that simmers for six hours has genuinely been off the shelf all
 * afternoon, and a count taken during it should agree with the ledger.
 */
export function useStartRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ProductionRun, ApiError, { runId: string }>({
    mutationFn: ({ runId }) => api.post<ProductionRun>(`/v1/production/runs/${runId}/start`),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Record what came out, and put it on a shelf.
 *
 * `actualYield` is required and should reach here from an empty field. The
 * batch is costed on what was actually made, so the value booked in equals the
 * value drawn out — a yield of zero is allowed and books nothing.
 */
export function useCompleteRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    ProductionRun,
    ApiError,
    {
      runId: string;
      actualYield: string;
      note?: string;
      /**
       * `actualYield` is in the shelf's unit (`stock_unit`). Only for a
       * `stranded` run; the server refuses it on one that can finish normally.
       */
      inStockUnit?: boolean;
    }
  >({
    mutationFn: ({ runId, actualYield, note, inStockUnit = false }) =>
      api.post<ProductionRun>(`/v1/production/runs/${runId}/complete`, {
        actual_yield: actualYield,
        note: note ?? null,
        in_stock_unit: inStockUnit,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Give up on a run under way. The ingredients are gone and stay gone.
 *
 * Nothing is reversed, because there is nothing to reverse. The reason is
 * required and is the whole value of recording it: "we lost £60 of stock" is a
 * number, "we lost £60 because the walk-in failed overnight" is actionable.
 */
export function useAbandonRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ProductionRun, ApiError, { runId: string; reason: string }>({
    mutationFn: ({ runId, reason }) =>
      api.post<ProductionRun>(`/v1/production/runs/${runId}/abandon`, { reason }),
    onSettled: () => invalidate(queryClient),
  });
}

/** Drop a planned run nobody got to. Costs nothing, because nothing moved. */
export function useCancelRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ProductionRun, ApiError, { runId: string }>({
    mutationFn: ({ runId }) => api.post<ProductionRun>(`/v1/production/runs/${runId}/cancel`),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Finished runs, worst yield first. Enterprise.
 *
 * Ranked by distance from the recipe in *absolute* terms, so a run that came
 * out 20% over sits at the top beside one that came out 20% short. Twelve
 * litres from a ten-litre recipe is not a good day — either the recipe is
 * wrong or somebody topped the pot up — and a report that only ranks
 * shortfalls never surfaces it.
 */
export function useYieldVariance(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['production', 'variance', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<ProductionRun[]>(`/v1/production/variance?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

/**
 * How a run's outturn should read on screen: "9 l — 10% short of 10 l".
 *
 * Stated here rather than in a component so the sign convention cannot drift
 * between a list and a detail panel. Under and over get different words
 * because they mean different things to the person reading them, and a bare
 * signed percentage makes a reader do that translation every time.
 */
export function describeYield(run: ProductionRun): string | null {
  if (run.actual_yield === null) return null;
  const made = `${Number(Number(run.actual_yield).toFixed(3))} ${run.actual_unit ?? run.yield_unit}`;
  // Measured in the shelf's unit because the recipe's could not be booked in:
  // there is no recipe figure in that unit to compare with, so say that
  // instead of leaving the gap unexplained.
  if (run.actual_unit !== null) return `${made} — weighed in ${run.actual_unit}, not compared`;
  if (run.yield_variance_percent === null) return made;

  const variance = Number(run.yield_variance_percent);
  if (variance === 0) return `${made} — on the recipe`;

  const expected = `${Number(Number(run.expected_yield).toFixed(3))} ${run.yield_unit}`;
  const direction = variance < 0 ? 'short of' : 'over';
  return `${made} — ${Math.abs(variance)}% ${direction} ${expected}`;
}
